/**
 * @fileoverview Artifact fetching and downloading operations.
 *
 * Handles retrieval of artifact summaries (form metadata extracted from documents)
 * and individual artifact data payloads with automatic retry logic and deadline-based
 * timeout management. All operations respect a global deadline to prevent indefinite
 * polling across multiple phases (status → summary → downloads).
 */

import { apiRequest } from 'platform/utilities/api';
import { buildOutputUrl, buildDownloadUrl } from './endpoints';
import { createCaveError } from './errors';
import { withRetry } from './withRetry';

const DEFAULT_INTERVAL_MS = 2000;

/**
 * Fetches the artifact summary (list of forms extracted from a document).
 *
 * Automatically retries on transient HTTP errors (5xx, 408, 429) but fails fast
 * on client errors (4xx except 408/429). Respects a global deadline across all
 * polling phases to prevent indefinite retries.
 *
 * @async
 * @param {string} documentId - The ID of the processed document
 * @param {object} [retryOptions={}] - Retry configuration
 * @param {number} [retryOptions.intervalMs=2000] - Milliseconds between retries
 * @param {number} globalDeadline - Absolute timestamp (ms) marking the deadline
 * @param {function} [shouldAbort] - Function that returns true if operation should abort
 * @returns {Promise<{forms: Array}>} The summary payload with forms array
 * @throws {Error} If global deadline is missing, exhausted before request begins, or
 *   on non-retriable errors (4xx client errors, invalid response shape, unrecognized
 *   response structure)
 */
export const fetchArtifactSummary = async (
  documentId,
  retryOptions = {},
  globalDeadline,
  shouldAbort,
) => {
  const { intervalMs = DEFAULT_INTERVAL_MS } = retryOptions;

  const response = await withRetry(
    () => apiRequest(buildOutputUrl(documentId, 'artifact')),
    {
      globalDeadline,
      shouldAbort,
      intervalMs,
      errorPrefix: 'CAVE artifact summary request failed',
      fallbackDetail: 'Unable to retrieve extracted document artifacts.',
      operationName: 'artifact summary fetch',
      timeoutMessage: 'Timed out while retrieving the extracted document summary. ' +
        'The processing service was slow to respond. Try uploading again.',
    },
  );

  // Shape guards are non-retriable — a malformed response shape is not a transient error.
  if (response === null || typeof response !== 'object') {
    throw createCaveError(new Error('Response is not an object'), {
      prefix: 'CAVE artifact summary: invalid response',
      fallbackDetail: 'Unable to retrieve extracted document artifacts.',
    });
  }

  if (!Array.isArray(response.forms)) {
    throw createCaveError(new Error('Missing forms array in response'), {
      prefix: 'CAVE artifact summary: missing forms array',
      fallbackDetail: 'Unable to retrieve extracted document artifacts.',
    });
  }

  return response;
};

/**
 * Downloads the actual artifact data (extracted form content) for a specific artifact.
 *
 * Automatically retries on transient HTTP errors (5xx, 408, 429) but fails fast
 * on client errors (4xx except 408/429). Respects a global deadline across all
 * operations to prevent indefinite retries.
 *
 * @async
 * @param {string} documentId - The ID of the processed document
 * @param {string} kvpId - The artifact validation ID from the summary
 * @param {object} [retryOptions={}] - Retry configuration
 * @param {number} [retryOptions.intervalMs=2000] - Milliseconds between retries
 * @param {number} globalDeadline - Absolute timestamp (ms) marking the deadline
 * @param {function} [shouldAbort] - Function that returns true if operation should abort
 * @returns {Promise<object|null>} The artifact data payload, or null if response is null
 * @throws {Error} If global deadline is missing, exhausted before request begins, or
 *   on non-retriable errors (4xx client errors, invalid response shape)
 */
export const downloadArtifactData = async (
  documentId,
  kvpId,
  retryOptions = {},
  globalDeadline,
  shouldAbort,
) => {
  const { intervalMs = DEFAULT_INTERVAL_MS } = retryOptions;

  const response = await withRetry(
    () =>
      apiRequest(buildDownloadUrl(documentId, kvpId), {
        headers: {
          'X-Key-Inflection': 'snake',
        },
      }),
    {
      globalDeadline,
      shouldAbort,
      intervalMs,
      errorPrefix: 'CAVE artifact download failed',
      fallbackDetail: 'Unable to retrieve extracted document artifact data.',
      operationName: 'artifact download',
      timeoutMessage: 'Timed out while downloading extracted document data. ' +
      'The processing service was slow to respond. Try uploading again.',
    },
  );

  // Shape guard is non-retriable.
  if (response !== null && typeof response !== 'object') {
    throw createCaveError(new Error('Response is not an object'), {
      prefix: 'CAVE artifact download: invalid response',
      fallbackDetail: 'Unable to retrieve extracted document artifact data.',
    });
  }

  return response;
};
